import React, { useMemo, useEffect } from 'react';
import { useAtom } from 'jotai';
import useTranslate from '../../Hooks/useTranslate';
import {
  TextField,
  FormControlLabel,
  Box,
  Typography,
  Grid,
  Paper,
  Divider,
} from '@mui/material';
import { makeGenericFieldAtom } from '../../store/genericFormStore';
import { DynamicSelect } from './DynamicSelect';
import Switch from './Switch';
import { SchemaRenderer } from './SchemaRenderer';

export const getFullPath = (basePath, key) =>
  basePath ? `${basePath}.${key}` : key;

export const StringField = ({ basePath, field }) => {
  const translate = useTranslate();
  const path = getFullPath(basePath, field.key);

  // FIX: Non passiamo più un defaultValue all'atomo.
  // Vogliamo che restituisca undefined se il campo non esiste fisicamente nello store!
  const fieldAtom = useMemo(() => makeGenericFieldAtom(path), [path]);
  const [value, setValue] = useAtom(fieldAtom);

  useEffect(() => {
    // Se è undefined, significa che nello Store globale non c'è nulla. Lo iniettiamo!
    if (value === undefined) {
      const fallbackValue =
        field.defaultValue !== undefined && field.defaultValue !== null
          ? field.defaultValue
          : '';
      setValue(fallbackValue, { isInit: true });
    }
  }, [value, field.defaultValue, setValue]);

  return (
    <TextField
      fullWidth
      type="text"
      label={translate(field.label)}
      value={value ?? ''}
      onChange={(e) => setValue(e.target.value)}
      required={field.required}
      size="small"
      margin="normal"
      inputProps={{ maxLength: field.maxLength }}
    />
  );
};

export const NumberField = ({ basePath, field }) => {
  const translate = useTranslate();
  const path = getFullPath(basePath, field.key);

  const fieldAtom = useMemo(() => makeGenericFieldAtom(path), [path]);
  const [value, setValue] = useAtom(fieldAtom);

  useEffect(() => {
    if (value === undefined) {
      setValue(field.defaultValue !== undefined ? field.defaultValue : null, {
        isInit: true,
      });
    }
  }, [value, field.defaultValue, setValue]);

  // Forza il valore tra min e max se l'utente digita fuori dai limiti
  useEffect(() => {
    if (value !== undefined && value !== null && value !== '') {
      if (field.min !== undefined && value < field.min) {
        setValue(field.min);
      } else if (field.max !== undefined && value > field.max) {
        setValue(field.max);
      }
    }
  }, [field.min, field.max, value, setValue]);

  return (
    <TextField
      fullWidth
      type="number"
      label={translate(field.label)}
      value={value ?? ''}
      onChange={(e) =>
        setValue(e.target.value === '' ? '' : Number(e.target.value))
      }
      required={field.required}
      size="small"
      margin="normal"
      InputProps={{ inputProps: { min: field.min, max: field.max } }}
    />
  );
};

export const BooleanField = ({ basePath, field }) => {
  const translate = useTranslate();
  const path = getFullPath(basePath, field.key);

  const fieldAtom = useMemo(() => makeGenericFieldAtom(path), [path]);
  const [value, setValue] = useAtom(fieldAtom);

  useEffect(() => {
    if (value === undefined) {
      setValue(field.defaultValue !== undefined ? field.defaultValue : false, {
        isInit: true,
      });
    }
  }, [value, field.defaultValue, setValue]);

  return (
    <Switch
      id={field.key}
      checked={!!value}
      onChange={(e) => setValue(e.target.checked)}
      label={translate(field.label)}
    />
  );
};

export const SelectField = ({ basePath, field }) => {
  const path = getFullPath(basePath, field.key);

  const fieldAtom = useMemo(() => makeGenericFieldAtom(path), [path]);
  const [value, setValue] = useAtom(fieldAtom);

  useEffect(() => {
    if (value === undefined) {
      setValue(field.defaultValue !== undefined ? field.defaultValue : '', {
        isInit: true,
      });
    }
  }, [value, field.defaultValue, setValue]);

  return <DynamicSelect field={field} value={value} onChange={setValue} />;
};

export const ObjectField = ({ basePath, field }) => {
  const path = getFullPath(basePath, field.key);
  const translate = useTranslate();

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 3,
        mb: 3,
        mt: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        borderColor: 'rgba(0,0,0,0.12)',
        boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
      }}
    >
      <Box sx={{ mb: 1 }}>
        <Typography variant="h6" color="primary.main" sx={{ fontWeight: 600 }}>
          {translate(field.label)}
        </Typography>
        <Divider sx={{ mt: 1 }} />
      </Box>

      <Box sx={{ pl: 1 }}>
        <SchemaRenderer fields={field.itemSchema || []} basePath={path} />
      </Box>
    </Paper>
  );
};

export const ConstantField = ({ basePath, field }) => {
  const translate = useTranslate();
  const path = getFullPath(basePath, field.key);

  const fieldAtom = useMemo(() => makeGenericFieldAtom(path), [path]);
  const [value, setValue] = useAtom(fieldAtom);

  useEffect(() => {
    const currentValueString = JSON.stringify(value ?? null);
    const expectedValueString = JSON.stringify(field.value ?? null);

    if (currentValueString !== expectedValueString) {
      setValue(field.value, { isInit: true });
    }
  }, [field.value, value, setValue]);

  // --- VISUALIZZAZIONE BOOLEANO ---
  if (typeof field.value === 'boolean') {
    return (
      <Box sx={{ mt: 2, mb: 1 }}>
        <Switch
          id={field.key}
          checked={!!value}
          disabled
          label={translate(field.label)}
        />
      </Box>
    );
  }

  // --- VISUALIZZAZIONE ARRAY ---
  if (Array.isArray(field.value)) {
    return (
      <Box sx={{ mt: 2, mb: 1 }}>
        <Typography
          variant="caption"
          color="textSecondary"
          sx={{ mb: 0.5, display: 'block', fontWeight: 600 }}
        >
          {translate(field.label)}
        </Typography>
        <Grid container spacing={1}>
          {(field.value || []).map((val, idx) => (
            <Grid item xs={6} key={idx}>
              <Paper
                sx={{
                  p: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  borderRadius: '6px',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    wordBreak: 'break-word',
                  }}
                >
                  {val}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  // --- VISUALIZZAZIONE TESTO / NUMERO ---
  return (
    <Box sx={{ mt: 2, mb: 1 }}>
      <Typography
        variant="caption"
        color="textSecondary"
        sx={{ mb: 0.5, display: 'block', fontWeight: 600 }}
      >
        {translate(field.label)}
      </Typography>
      <Box
        sx={{
          p: '12px',
          borderRadius: '8px',
          minHeight: '40px',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Typography variant="body1" sx={{ fontWeight: 500 }}>
          {value ?? ''}
        </Typography>
      </Box>
    </Box>
  );
};
